import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Pagination, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTrash, FaTimes, FaSearch } from 'react-icons/fa';
import { useUserAuth } from '../../context/UserAuthContext';
import axios from 'axios';
import './Admin.css';

const API_BASE_URI = process.env.REACT_APP_API_BASE_URI;

const WishManagement = () => {
  const [wishes, setWishes] = useState([]);
  const [pendingWishes, setPendingWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'pending'
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const wishesPerPage = 10;

  useEffect(() => {
    fetchWishes();
    fetchPendingWishes();
  }, []);

  const fetchWishes = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/wishes?limit=100`, { headers });
      
      setWishes(response.data.wishes || []);
      
      // Calculate total pages
      const total = response.data.wishes?.length || 0;
      setTotalPages(Math.ceil(total / wishesPerPage));
      
    } catch (err) {
      console.error('Error fetching wishes:', err);
      setError('Failed to load wishes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingWishes = async () => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/wishes/pending`, { headers });
      
      setPendingWishes(response.data.wishes || []);
      
    } catch (err) {
      console.error('Error fetching pending wishes:', err);
      // We don't set the main error here to avoid disrupting the UI if only this request fails
    }
  };

  const handleApproveWish = async (wishId) => {
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      await axios.patch(
        `${API_BASE_URI}/admin/wishes/${wishId}/approve`, 
        { approved: true },
        { headers }
      );

      // Update pending wishes list
      setPendingWishes(pendingWishes.filter(wish => wish.id !== wishId));
      
      // Refresh all wishes
      fetchWishes();
      
    } catch (err) {
      console.error('Error approving wish:', err);
      setError('Failed to approve wish');
    }
  };

  const handleRejectWish = async (wishId) => {
    if (window.confirm(t('adminDashboard.confirmRejectWish'))) {
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.patch(
          `${API_BASE_URI}/admin/wishes/${wishId}/approve`, 
          { approved: false },
          { headers }
        );

        // Update pending wishes list
        setPendingWishes(pendingWishes.filter(wish => wish.id !== wishId));
        
        // Update all wishes list
        setWishes(wishes.filter(wish => wish.id !== wishId));
        
      } catch (err) {
        console.error('Error rejecting wish:', err);
        setError('Failed to reject wish');
      }
    }
  };

  const handleDeleteWish = async (wishId) => {
    if (window.confirm(t('adminDashboard.confirmDeleteWish'))) {
      try {
        const token = await user.getIdToken();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.delete(`${API_BASE_URI}/wishes/${wishId}`, { headers });

        // Update both lists
        setPendingWishes(pendingWishes.filter(wish => wish.id !== wishId));
        setWishes(wishes.filter(wish => wish.id !== wishId));
        
      } catch (err) {
        console.error('Error deleting wish:', err);
        setError('Failed to delete wish');
      }
    }
  };

  // Filter wishes based on search term
  const filteredWishes = wishes.filter(wish => 
    wish.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wish.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get current page of wishes
  const currentWishes = filteredWishes.slice(
    (activePage - 1) * wishesPerPage,
    activePage * wishesPerPage
  );

  // Create pagination items
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === activePage}
        onClick={() => setActivePage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <div className="wish-management">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('adminDashboard.wishManagement')}</h2>
        <div>
          <Button 
            variant={viewMode === 'all' ? 'primary' : 'outline-primary'} 
            className="me-2"
            onClick={() => setViewMode('all')}
          >
            {t('adminDashboard.allWishes')} 
            <Badge bg="light" text="dark" className="ms-2">{wishes.length}</Badge>
          </Button>
          <Button 
            variant={viewMode === 'pending' ? 'warning' : 'outline-warning'} 
            onClick={() => setViewMode('pending')}
          >
            {t('adminDashboard.pendingApproval')} 
            <Badge bg="light" text="dark" className="ms-2">{pendingWishes.length}</Badge>
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {viewMode === 'pending' && pendingWishes.length === 0 && (
        <Alert variant="info">{t('adminDashboard.noPendingWishes')}</Alert>
      )}

      {viewMode === 'pending' && pendingWishes.length > 0 && (
        <Card className="mb-4">
          <Card.Header>{t('adminDashboard.pendingWishes')}</Card.Header>
          <Card.Body>
            {pendingWishes.map(wish => (
              <div key={wish.id} className="pending-wish-card">
                <div className="pending-wish-content">
                  <div className="pending-wish-header">
                    <h5>{wish.userName}</h5>
                    <small className="text-muted">
                      {new Date(wish.createdAt).toLocaleDateString()} {new Date(wish.createdAt).toLocaleTimeString()}
                    </small>
                  </div>
                  <p className="pending-wish-message">{wish.message}</p>
                </div>
                <div className="pending-wish-actions">
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="me-2"
                    onClick={() => handleApproveWish(wish.id)}
                  >
                    <FaCheck />
                  </Button>
                  <Button 
                    variant="danger" 
                    size="sm"
                    onClick={() => handleRejectWish(wish.id)}
                  >
                    <FaTimes />
                  </Button>
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {viewMode === 'all' && (
        <>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                {t('adminDashboard.allWishes')}
                <InputGroup className="search-input">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t('adminDashboard.searchWishes')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('loading')}</span>
                  </Spinner>
                </div>
              ) : currentWishes.length === 0 ? (
                <Alert variant="info">
                  {searchTerm 
                    ? t('adminDashboard.noWishMatchSearch') 
                    : t('adminDashboard.noWishesYet')
                  }
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>{t('adminDashboard.user')}</th>
                        <th>{t('adminDashboard.message')}</th>
                        <th>{t('adminDashboard.date')}</th>
                        <th>{t('adminDashboard.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentWishes.map(wish => (
                        <tr key={wish.id}>
                          <td>{wish.userName}</td>
                          <td className="wish-message-cell">
                            {wish.message}
                          </td>
                          <td>{new Date(wish.createdAt).toLocaleDateString()}</td>
                          <td>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDeleteWish(wish.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer>
              <Pagination className="mb-0 justify-content-center">
                <Pagination.First onClick={() => setActivePage(1)} disabled={activePage === 1} />
                <Pagination.Prev onClick={() => setActivePage(Math.max(1, activePage - 1))} disabled={activePage === 1} />
                {paginationItems}
                <Pagination.Next onClick={() => setActivePage(Math.min(totalPages, activePage + 1))} disabled={activePage === totalPages} />
                <Pagination.Last onClick={() => setActivePage(totalPages)} disabled={activePage === totalPages} />
              </Pagination>
            </Card.Footer>
          </Card>
        </>
      )}
    </div>
  );
};

export default WishManagement;